import { dayjs } from '@/extendedDayjs';
import {
    AllReleaseStepTypes,
    ReleaseStepType,
    TimelineEvent,
    TimelineEventBatchActionRequestedData,
    TimelineEventType,
} from '../../types';
import { Nullable } from '@/typescriptHelpers';

export const TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE: Record<ReleaseStepType, TimelineEventType[]> = {
    [ReleaseStepType.Kickoff]: [
        TimelineEventType.ReleaseCreated,
        TimelineEventType.ReleaseCreatedUpcomingRelease,
        TimelineEventType.ReleaseVersionBumped,
        TimelineEventType.ReleaseBranchCreated,
        TimelineEventType.ReleaseCodePromoted,
        TimelineEventType.ReleaseTargetKickoffDateUpdated,
        TimelineEventType.ReleaseTargetSubmissionDateUpdated,
        TimelineEventType.ReleaseTargetReleaseDateUpdated,
        TimelineEventType.ReleaseVersionUpdated,
        TimelineEventType.ReleasePilotChanged,
        TimelineEventType.TargetKickoffDateUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
        TimelineEventType.FeatureFlagToggled,
    ],
    [ReleaseStepType.Submission]: [
        TimelineEventType.ReviewSubmissionItemAdded,
        TimelineEventType.ReviewSubmissionItemRemoved,
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
        TimelineEventType.ReleaseSubmitted,
        TimelineEventType.ReleaseStepsReadyToSubmit,
        TimelineEventType.ReleaseTargetSubmissionDateUpdated,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.AppStoreVersionAppStoreBuildChanged,
        TimelineEventType.TargetSubmissionDateUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
    ],
    [ReleaseStepType.Takeoff]: [
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
        TimelineEventType.ReleaseReleased,
        TimelineEventType.ReleaseTagged,
        TimelineEventType.ReleaseStepsReadyToRelease,
        TimelineEventType.ReleaseTargetReleaseDateUpdated,
        TimelineEventType.ReleaseVersionUpdated,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.AppStoreVersionPhasedReleaseUpdated,
        TimelineEventType.AppStoreVersionReleaseNotesUpdated,
        TimelineEventType.TargetReleaseDateUpcoming,
        TimelineEventType.ReleaseSchedulePaused,
        TimelineEventType.ReleaseScheduleResumed,
    ],
    [ReleaseStepType.ReleaseCandidate]: [
        TimelineEventType.ReleaseStepRCBuildStatusChanged,
        TimelineEventType.ReleaseStepCIBuildFailureOverridden,
        TimelineEventType.BinaryArtifactAppStoreUploadFailed,
        TimelineEventType.BinaryArtifactAppStoreUploadSuccess,
        TimelineEventType.DsymArtifactStabilityMonitoringUploadFailed,
        TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
    ],
    [ReleaseStepType.RegressionTesting]: [TimelineEventType.ReleaseStepRegressionTestingStatusChanged],
    [ReleaseStepType.BetaTesting]: [
        TimelineEventType.ReleaseStepBetaSoakStarted,
        TimelineEventType.ReleaseStepBetaSoakFinished,
        TimelineEventType.AppStoreVersionBetaSubmittedForReview,
    ],
    [ReleaseStepType.Metadata]: [
        TimelineEventType.ReleaseStepMetadataApprovalChanged,
        TimelineEventType.AppStoreVersionReleaseNotesUpdated,
        TimelineEventType.MetadataTranslationsExported,
        TimelineEventType.MetadataTranslationsUploaded,
        TimelineEventType.SyncedVCSMetadata,
    ],
    [ReleaseStepType.Translations]: [
        TimelineEventType.ReleaseStepTranslationsApprovalChanged,
        TimelineEventType.TranslationsUploaded,
        TimelineEventType.TranslationsExported,
        TimelineEventType.TranslationsUpdated,
    ],
    [ReleaseStepType.Screenshots]: [
        TimelineEventType.ReleaseStepScreenshotsApprovalChanged,
        TimelineEventType.ReleaseStepScreenshotAdded,
        TimelineEventType.ReleaseStepScreenshotDeleted,
        TimelineEventType.RunwayEventReleaseStepAppPreviewAdded,
        TimelineEventType.RunwayEventReleaseStepAppPreviewDeleted,
    ],
    [ReleaseStepType.FeatureReadiness]: [TimelineEventType.VCSCherryPickConflict],
    [ReleaseStepType.Approvals]: [],
    [ReleaseStepType.StoreReview]: [
        TimelineEventType.ReleaseSubmitted,
        TimelineEventType.ReleaseMarkedRejected,
        TimelineEventType.AppStoreVersionStatusUpdated,
        TimelineEventType.ReviewSubmissionItemStatusUpdated,
    ],
    [ReleaseStepType.CIDistribution]: [],
    [ReleaseStepType.VCSDistribution]: [],
    [ReleaseStepType.RollbackBuild]: [
        TimelineEventType.ResignSequenceStarted,
        TimelineEventType.ResignSequenceCompleted,
        TimelineEventType.BuildResignStarted,
        TimelineEventType.BuildResignCompleted,
    ],
    [ReleaseStepType.Rollout]: [],
    [ReleaseStepType.ReleaseCycle]: [],
};

export const RELEASE_STEP_TYPE_BY_EVENT_TYPE: Record<TimelineEventType, ReleaseStepType> = AllReleaseStepTypes.reduce(
    (acc, releaseStepType) => {
        const eventTypes = TIMELINE_EVENT_TYPES_BY_RELEASE_STEP_TYPE[releaseStepType];
        eventTypes.forEach((eventType) => {
            acc[eventType] = releaseStepType;
        });
        return acc;
    },
    {} as Record<TimelineEventType, ReleaseStepType>,
);

const PRIORITY_ORDER = [
    TimelineEventType.ReleaseStepRegressionTestingStatusChanged,
    TimelineEventType.ReleaseStepStatusChanged,
    TimelineEventType.ReleaseStepChecklistCompleted,
    TimelineEventType.ChecklistItemStatusUpdated,
];

export const sortTimelineEvents = (events: TimelineEvent[] = []) => {
    return [...events].sort((a, b) => {
        const diffInMilliseconds = dayjs(b.triggeredAt).diff(dayjs(a.triggeredAt));
        const parallelEventsThresholdInMilliseconds = 10 * 1000;
        if (Math.abs(diffInMilliseconds) <= parallelEventsThresholdInMilliseconds) {
            const indexOfA = PRIORITY_ORDER.indexOf(a.type);
            const indexOfB = PRIORITY_ORDER.indexOf(b.type);

            // to handle events that are not in the PRIORITY_ORDER
            if (indexOfA === -1 && indexOfB === -1) {
                return 0;
            } else if (indexOfA === -1) {
                return 1;
            } else if (indexOfB === -1) {
                return -1;
            }

            return indexOfA - indexOfB;
        }

        return diffInMilliseconds;
    });
};

export const batchActionSummaryInfosFromBatchTimelineEventData = (
    data: Nullable<TimelineEventBatchActionRequestedData>,
) => {
    const lastAttemptedAt = data?.lastAttemptedAt;

    const maxAttempts = data?.maxAttempts;

    const targetStepDisplayNames = data?.targetStepDisplayNames;
    const successesStepDisplayNames = data?.successesStepDisplayNames;
    const pendingRetryStepDisplayNames = data?.pendingRetryStepDisplayNames;
    const failuresStepDisplayNames = data?.failuresStepDisplayNames;

    const successfulForStepRunIds = data?.succeededForStepRunIds;
    const pendingRetryForStepRunIds = data?.pendingRetryForStepRunIds;
    const failedForStepRunIds = data?.failedForStepRunIds;

    const successfulForDestStepConfigIds = data?.successesDestStepConfigIds;
    const pendingRetryForDestStepConfigIds = data?.pendingRetryDestStepConfigIds;
    const failedForDestStepConfigIds = data?.failuresDestStepConfigIds;

    const anySuccessful = successfulForStepRunIds?.length > 0;
    const anyPendingRetry = pendingRetryForStepRunIds?.length > 0;
    const anyFailed = failedForStepRunIds?.length > 0;

    const retryExpected = anyPendingRetry;

    return {
        lastAttemptedAt,
        maxAttempts,
        targetStepDisplayNames,
        successesStepDisplayNames,
        pendingRetryStepDisplayNames,
        failuresStepDisplayNames,
        successfulForStepRunIds,
        pendingRetryForStepRunIds,
        failedForStepRunIds,
        successfulForDestStepConfigIds,
        pendingRetryForDestStepConfigIds,
        failedForDestStepConfigIds,
        anySuccessful,
        anyPendingRetry,
        anyFailed,
        retryExpected,
    };
};
